/* eslint-disable consistent-return */
import { groupBy, isUndefined, uniqBy, last, orderBy, includes, findIndex, minBy, maxBy, keys } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Card from '../../../components/card/Card'
import { MEASURE_COTE } from '../../../piezometry/constants/PiezometryConstants'
import PiezometerStationAction from '../../../station/actions/PiezometerStationAction'
import { calculateValue, getRemarks } from '../../../utils/AnalyseUtils'
import { getDate } from '../../../utils/DateUtil'
import useListIndexed from 'utils/customHook/useListIndexed'
import AnalysisAction from 'quality/actions/AnalysisAction'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import { DAY } from 'quality/constants/ChartConstant'
import QualityChart, { BAR, LINE } from 'components/echart/QualityChart'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import { canShowStationTypeData, getSettingJson } from 'utils/SettingUtils'
import { execByType } from 'utils/StationUtils'
import { getHardPiezoDataTypes } from 'utils/PiezometryUtils'
import StationAction from 'station/actions/StationAction'
import ProgressCard from 'components/card/ProgressCard'
import { DATA_TYPE } from 'pluviometry/constants/PluviometryConstant'
import { getColorFromPalettePluvio } from 'utils/ColorUtil'
import { SUM_AUTO } from 'pluviometry/constants/PluvioOptionConstant'

const DATATYPES_FOR_TAB = [DATA_TYPE.VIRTUAL_EFFECTIVE_RAIN, DATA_TYPE.RAIN, DATA_TYPE.ETP]
const BLUE_RAIN = '#0000ff'
const BLUE_ETP = '#009dbd'

const IndicatorsChart = ({

}) => {
    const dispatch = useDispatch()
    const {
        qualitometer,
        qualitometers,
        parameters,
        units,
        qualityIndicators,
        associatedSites,
        applicationSettings,
        piezoDataTypes,
        hydroDataTypes,
        pluvioDataTypes,
    } = useSelector(store => ({
        qualitometer: store.QualityReducer.qualitometer,
        qualitometers: store.QualityReducer.qualitometersLight,
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        qualityIndicators: store.QualityReducer.qualityIndicators,
        associatedSites: store.StationReducer.associatedSites,
        applicationSettings: store.AdministrationReducer.applicationSettings,
        piezoDataTypes: store.StationReducer.piezoDataTypes,
        hydroDataTypes: store.StationReducer.hydroDataTypes,
        pluvioDataTypes: store.StationReducer.pluvioDataTypes,
    }), shallowEqual)

    const unitsIndex = useListIndexed(units, 'code')
    const parametersIndex = useListIndexed(parameters, 'code')
    const qualitometersIndex = useListIndexed(qualitometers, 'id')

    const indicator = useMemo(() => {
        return qualityIndicators.find(qi => `${qi.stationType}` === qualitometer.stationType)
    }, [qualitometer.stationType, qualityIndicators])


    const orderedParameters = useMemo(() => indicator?.indicators?.map(i => `${i.id}`) ?? [], [indicator?.indicators])

    const [analysis, setAnalysis] = useState([])
    const [dataTypesLoaded, setDataTypesLoaded] = useState(false)

    useEffect(() => {
        dispatch(StationAction.fetchDataTypesByProject()).then(() => setDataTypesLoaded(true))
    }, [])

    useEffect(() => {
        if (!isUndefined(qualitometer.id) && !orderedParameters.length) {
            return
        }
        setAnalysis(list => list.filter(a => qualitometer.id !== a.qualitometer))
        const filter = {
            lightMode: true,
            stations: [qualitometer.id],
            parameters: orderedParameters,
        }
        AnalysisAction.getAnalysis(filter).then((list = []) => setAnalysis(prevList => [
            ...prevList,
            ...list.map(a => new DtoAnalysisLight(a)),
        ]))
    }, [dispatch, orderedParameters, qualitometer.id])

    useEffect(() => {
        const listId = associatedSites.filter(a => a.stationLinkedType === 3).map(site => site.stationLinkedId)
        if (!listId.length && !orderedParameters.length) {
            return
        }

        setAnalysis([])

        const controller = new AbortController()

        const filter = {
            lightMode: true,
            stations: listId,
            parameters: orderedParameters,
        }
        AnalysisAction.getAnalysis(filter, controller.signal).then((list = []) => setAnalysis(prevList => [
            ...prevList,
            ...list.map(a => new DtoAnalysisLight(a)),
        ]))

        return () => {
            controller?.abort()
        }
    }, [associatedSites, dispatch, orderedParameters])

    const [piezoMeasures, setPiezoMeasures] = useState([])
    const [hydroMeasures, setHydroMeasures] = useState([])
    const [pluvioMeasures, setPluvioMeasures] = useState([])
    const hasPiezoMeasures = useMemo(() => piezoMeasures.some(m => m.measures?.length), [piezoMeasures])
    const hasHydroMeasures = useMemo(() => hydroMeasures.some(m => m.measures?.length), [hydroMeasures])
    const hasPluvioMeasures = useMemo(() => pluvioMeasures.some(m => m.measures?.length), [pluvioMeasures])

    const rules = getSettingJson(applicationSettings, 'showDataTypesRules') || {}
    const getDataTypes = (stationType) => execByType(stationType, {
        quality: () => [],
        piezometry: () => uniqBy(orderBy([...getHardPiezoDataTypes(), ...piezoDataTypes ], 'id'), 'id'),
        hydrometry: () => orderBy(hydroDataTypes, 'id'),
        pluviometry: () => orderBy(pluvioDataTypes, 'id'),
    })

    useEffect(() => {
        if (dataTypesLoaded && analysis.length) {
            const minDate = minBy(analysis, ({ sampleDate }) => sampleDate).sampleDate
            const maxDate = maxBy(analysis, ({ sampleDate }) => sampleDate).sampleDate
            const listPiezoId = associatedSites.filter(a => a.stationLinkedType === STATION_TYPE_CONSTANT.PIEZOMETER).map(site => site.stationLinkedId)
            if (listPiezoId.length) {
                getDataTypes('PIEZO').map(dataType => {
                    if (canShowStationTypeData('QUALITO', 'PIEZO', dataType.id, rules)) {
                        const filters = listPiezoId.map(id => ({
                            stationId: id,
                            displayCote: MEASURE_COTE.NGF,
                            groupFunc: 'MAX',
                            dataType: dataType.id,
                            startDate: minDate,
                            endDate: maxDate,
                        }))
                        PiezometerStationAction.getPiezosChartMeasures(filters).then(measures => {
                            measures.map(measure => {
                                if (measure.measures.find(({ value }) => value !== null)) {
                                    setPiezoMeasures(prev => [...prev, { ...measure, dataType }])
                                }
                            })
                        })
                    }
                })
            }

            const listHydroId = associatedSites.filter(a => a.stationLinkedType === STATION_TYPE_CONSTANT.HYDROMETRIC_STATION).map(site => site.stationLinkedId)
            if (listHydroId.length) {
                getDataTypes('HYDRO').map(dataType => {
                    if (canShowStationTypeData('QUALITO', 'HYDRO', dataType.id, rules)) {
                        const filters = listHydroId.map(id => ({
                            stationId: id,
                            groupFunc: 'MAX',
                            chartMode: true,
                            dataType: dataType.id,
                            startDate: minDate,
                            endDate: maxDate,
                        }))
                        HydrometryAction.getHydrosChronicMeasures(filters).then(measures => {
                            measures.map(measure => {
                                if (measure.measures.length) {
                                    setHydroMeasures(prev => [...prev, { ...measure, dataType }])
                                }
                            })
                        })
                    }
                })
            }

            const listPluvioId = associatedSites.filter(a => a.stationLinkedType === STATION_TYPE_CONSTANT.PLUVIOMETRY).map(site => site.stationLinkedId)
            const defaultPluvioInput = {
                chartMode: true,
                startDate: minDate,
                endDate: maxDate,
            }
            if (listPluvioId.length) {
                getDataTypes('PLUVIO').map(dataType => {
                    if (canShowStationTypeData('QUALITO', 'PLUVIO', dataType.id, rules)) {
                        listPluvioId.map(id =>
                            dispatch(PluviometryAction.getPluvioChronicMeasures({ ...defaultPluvioInput, groupFunc: includes(DATATYPES_FOR_TAB, dataType.id) ? SUM_AUTO : 'MAX', stationId: id, dataType: dataType.id })).then(measures => {
                                if (measures.measures.length) {
                                    setPluvioMeasures(prev => [...prev, { ...measures, dataType }])
                                }
                            }),
                        )
                    }
                })
            }
        }

        const controller = new AbortController()

        return () => {
            controller?.abort()
        }
    }, [associatedSites, dataTypesLoaded, analysis])

    if (!dataTypesLoaded) {
        return <ProgressCard indeterminate />
    }

    const analysisGroup = groupBy(analysis, 'parameter')
    const nbParameter = orderedParameters.length

    const grids = orderedParameters.map(param => ({
        gridId: param,
        height: 150,
    }))

    const xAxis = orderedParameters.map((param, index) => ({
        gridId: param,
        xAxisId: param,
        ...(hasPiezoMeasures || index !== nbParameter - 1 ? {
            axisLine: { show: false },
            axisLabel: { show: false },
            axisTick: { show: false },
        } : {}),
    }))

    const yAxis = orderedParameters.map(param => {
        const parameterLabel = parametersIndex[param]?.displayName
        const parameterAnalysis = analysis.filter(a => a.parameter === param)
        const uniqUnits = uniqBy(parameterAnalysis, 'unit').map(a => a.unit)
        const unitsLabel = uniqUnits.map(unit => unitsIndex[unit]?.symbol).filter(u => !!u).join(', ')

        return {
            gridId: param,
            yAxisId: param,
            name: `${parameterLabel} (${unitsLabel})`,
        }
    })

    const piezoGrids = hasPiezoMeasures ? piezoMeasures.map((_, i) => ({
        gridId: `piezo-${i}`,
        height: 150,
    })) : []

    const hydroGrids = hasHydroMeasures ? hydroMeasures.map((_, i) => ({
        gridId: `hydro-${i}`,
        height: 150,
    })) : []

    const pluvioGrids = hasPluvioMeasures ? pluvioMeasures.map((_, i) => ({
        gridId: `pluvio-${i}`,
        height: 150,
    })) : []

    const piezoXAxis = hasPiezoMeasures ? piezoMeasures.map((_, i) => ({
        gridId: `piezo-${i}`,
        xAxisId: `piezo-${i}`,
    })) : []

    const hydroXAxis = hasHydroMeasures ? hydroMeasures.map((_, i) => ({
        gridId: `hydro-${i}`,
        xAxisId: `hydro-${i}`,
    })) : []

    const pluvioXAxis = hasPluvioMeasures ? pluvioMeasures.map((_, i) => ({
        gridId: `pluvio-${i}`,
        xAxisId: `pluvio-${i}`,
    })) : []

    const piezoYAxis = hasPiezoMeasures ? piezoMeasures.map((piezo, i) => ({
        type: 'value',
        name: `${piezo.dataType.name ?? piezo.dataType.label}${piezo.dataType.unit ? ` (${piezo.dataType.unit})` : ''}`,
        gridId: `piezo-${i}`,
        yAxisId: `piezo-${i}`,
    })) : []

    const hydroYAxis = hasHydroMeasures ? hydroMeasures.map((hydro, i) => ({
        type: 'value',
        name: `${hydro.dataType.name ?? hydro.dataType.label}${hydro.dataType.unit ? ` (${hydro.dataType.unit})` : ''}`,
        gridId: `hydro-${i}`,
        yAxisId: `hydro-${i}`,
    })) : []

    const pluvioYAxis = hasPluvioMeasures ? pluvioMeasures.map((pluvio, i) => ({
        type: 'value',
        name: `${pluvio.dataType.name ?? pluvio.dataType.label}${pluvio.dataType.unit ? ` (${pluvio.dataType.unit})` : ''}`,
        gridId: `pluvio-${i}`,
        yAxisId: `pluvio-${i}`,
    })) : []

    const datas = orderedParameters.flatMap(param => {
        const analysisFiltered = analysisGroup[param] || []
        const groupByStation = groupBy(analysisFiltered, 'qualitometer')

        return Object.keys(groupByStation).map(key => {
            const qualitoAnalysis = groupByStation[key]
            const stationId = qualitoAnalysis[0].qualitometer

            const qualito = qualitometersIndex[stationId]

            return {
                name: `[${qualito?.code}] ${qualito?.name || ''}`,
                xAxisId: param,
                yAxisId: param,
                dataList: qualitoAnalysis.map(a => ({
                    date: a.sampleDate,
                    value: a.result,
                    result: a.result,
                    parameter: a.parameter,
                    remark: a.remark,
                    unit: unitsIndex[a.unit]?.symbol || i18n.unknownUnit,
                    marker: a.remark !== '1' ? getRemarks(a.remark)?.plot : 'none',
                })),
            }
        })
    })

    const piezoDatas = hasPiezoMeasures ? piezoMeasures.map((piezoMeasure, i) => {
        const piezoLink = associatedSites.find(l => l.stationLinkedType === STATION_TYPE_CONSTANT.PIEZOMETER && l.stationLinkedId === piezoMeasure.stationId)
        return {
            name: `[${piezoLink?.stationLinkedCode}] ${piezoLink?.stationLinkedName || ''}`,
            xAxisId: `piezo-${i}`,
            yAxisId: `piezo-${i}`,
            color: piezoMeasure.dataType.color,
            dataList: piezoMeasure.measures.map(m => ({
                date: m.date,
                value: m.value,
                result: m.value,
                name: piezoMeasure.dataType.name ?? piezoMeasure.dataType.label,
                unit: piezoMeasure.dataType.unit ?? '',
                marker: 'none',
            })),
        }
    }) : []

    const hydroDatas = hasHydroMeasures ? hydroMeasures.map((hydroMeasure, i) => {
        const hydroLink = associatedSites.find(l => l.stationLinkedType === STATION_TYPE_CONSTANT.HYDROMETRIC_STATION && l.stationLinkedId === hydroMeasure.stationId)
        return {
            name: `[${hydroLink?.stationLinkedCode}] ${hydroLink?.stationLinkedName || ''}`,
            xAxisId: `hydro-${i}`,
            yAxisId: `hydro-${i}`,
            color: hydroMeasure.dataType.color,
            dataList: hydroMeasure.measures.map(m => ({
                date: m.date,
                value: m.value,
                result: m.value,
                name: hydroMeasure.dataType.name ?? hydroMeasure.dataType.label,
                unit: hydroMeasure.dataType.unit ?? '',
                marker: 'none',
            })),
        }
    }) : []

    const getPluvioColor = (idDataType) => {
        switch (idDataType) {
            case DATA_TYPE.RAIN: {
                return BLUE_RAIN
            }
            case DATA_TYPE.ETP: {
                return BLUE_ETP
            }
            default: {
                return getColorFromPalettePluvio(findIndex(orderBy(pluvioDataTypes, 'id'), ({ id }) => id === idDataType))
            }
        }
    }

    const pluvioDatas = hasPluvioMeasures ? pluvioMeasures.map((pluvioMeasure, i) => {
        const pluvioLink = associatedSites.find(l => l.stationLinkedType === STATION_TYPE_CONSTANT.PLUVIOMETRY && l.stationLinkedId === pluvioMeasure.stationId)
        return {
            name: `[${pluvioLink?.stationLinkedCode}] ${pluvioLink?.stationLinkedName || ''}`,
            type: includes(DATATYPES_FOR_TAB, pluvioMeasure.dataType.id) ? BAR : LINE,
            xAxisId: `pluvio-${i}`,
            yAxisId: `pluvio-${i}`,
            color: pluvioMeasure.dataType.color || getPluvioColor(pluvioMeasure.dataType.id),
            dataList: pluvioMeasure.measures.map(m => ({
                date: m.date,
                value: m.value,
                result: m.value,
                name: pluvioMeasure.dataType.name ?? pluvioMeasure.dataType.label,
                unit: pluvioMeasure.dataType.unit ?? '',
                marker: 'none',
            })),
        }
    }) : []

    return !!analysis.length && (
        <Card round>
            <QualityChart
                grids={[...grids, ...piezoGrids, ...hydroGrids, ...pluvioGrids]}
                xAxis={[...xAxis, ...piezoXAxis, ...hydroXAxis, ...pluvioXAxis]}
                yAxis={[...yAxis, ...piezoYAxis, ...hydroYAxis, ...pluvioYAxis]}
                series={[...datas, ...piezoDatas, ...hydroDatas, ...pluvioDatas]}

                headerHeight={0}
                footerHeight={120}
                xAxisSpace={DAY}

                roundValue={3}

                withToolTypeLine
                withToolTypeBar
                // withToolTypeStack
                withToolLog
                withToolThreshold
                withToolMarker
                withToolLine
                withToolLegend

                withDataZoom
                withHidingGrid
                withHidingYAxis

                dataZoomPosition={{ bottom: 60 }}
                legendPosition={{ bottom: 10, right: 20, top: undefined }}
                toolboxPosition={{ top: 5 }}

                tooltipFormatter={params => {
                    const groupDate = groupBy(params, p => getDate(p.data.value[0]))
                    return keys(groupDate).map(key => {
                        const groupStation = groupBy(groupDate[key], 'seriesName')
                        const label = keys(groupStation).map(stationCode => {
                            const labelParam = groupStation[stationCode].map(({ marker, value, data: { unit = '' } }) => {
                                const obj = last(value)
                                return `${marker} ${obj.name ?? parametersIndex[obj.parameter]?.displayLabel} : ${calculateValue(obj)} ${(unit)}`
                            }).join('<br/>')
                            return `${stationCode}<br/>${labelParam}`
                        }).join('<br/><br/>')
                        return `${key}<br/>${label}`
                    }).join('<br/><br/>')
                }}
            />
        </Card>
    )
}

export default IndicatorsChart
